import React, { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  flexRender,
} from '@tanstack/react-table'
import EmailDetailsModal from './EmailDetailsModal.component'
import './EmailHistoryTable.style.scss'
import Pagination from "../CommonPage/Pagination.component";
import { startCase } from 'lodash'
import moment from "moment"
import logo from "../../image/Prime-Logo-Coloured-Vertical.png";

export default function EmailHistoryTable({ data }) {
  const [sorting, setSorting] = useState([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedEmail, setSelectedEmail] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 10

  const handleShowDetails = (email) => {
    setSelectedEmail(email)
  }
  const handleCloseModal = () => {
    setSelectedEmail(null)
  }

  const isLoading = data?.loading
  const rawData = data?.items?.Data || []
  const transformedData = rawData.map(item => ({
    sender: item.SenderEmailAddress,
    recipients: item.ReceiverEmailAddress,
    subject: item.Subject,
    date: moment(item.EventDate).format('DD-MMM-YYYY h:mm A'),
    status: startCase(item.Event),
    fullData: item
  }))

  const filteredData = useMemo(() => {
    let filtered = transformedData
    if (globalFilter) {
      const lower = globalFilter.toLowerCase()
      filtered = filtered.filter(row =>
        Object.values(row).some(val => val && val.toString().toLowerCase().includes(lower))
      )
    }
    return filtered
  }, [transformedData, globalFilter])

  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return filteredData.slice(start, start + pageSize)
  }, [filteredData, currentPage])

  const columns = useMemo(() => [
    { header: 'Sender', accessorKey: 'sender' },
    { header: 'Recipients', accessorKey: 'recipients' },
    { header: 'Subject', accessorKey: 'subject' },
    { header: 'Date', accessorKey: 'date' },
    {
      header: 'Status',
      accessorKey: 'status',
      cell: ({ getValue }) => getValue() === 'Open'
        ? <span className="status-pill read">Open</span>
        : <span className="status-pill unread">{getValue()}</span>
    },
    {
      id: 'details',
      header: '',
      cell: ({ row }) => (
        <button
          className="details-icon-btn"
          onClick={() => handleShowDetails(row.original.fullData)}
          aria-label="View Email Details"
        >
          <i className="bi bi-eye"></i>
        </button>
      ),
      enableSorting: false,
    }
  ], [])

  const table = useReactTable({
    data: paginatedData,
    columns,
    state: { sorting },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onSortingChange: setSorting,
  })

  return (
    <div className="email-history-table">
      <div className="email-history-controls">
        <input
          className="search-input"
          type="text"
          placeholder="Search..."
          value={globalFilter ?? ''}
          onChange={(e) => setGlobalFilter(e.target.value)}
        />
      </div>

      <table className="styled-table">
        <thead>
          {table.getHeaderGroups().map(headerGroup => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map(header => {
                const isSorted = header.column.getIsSorted()
                return (
                  <th key={header.id} onClick={header.column.getToggleSortingHandler()}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                    <i className={`bi ${isSorted === 'asc' ? 'bi-sort-down' : isSorted === 'desc' ? 'bi-sort-up' : ''} sort-icon`}></i>
                  </th>
                )
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={columns.length} className="email-table-loading">
                <div className="loading-logo">
                  <img src={logo} width="180" alt="Prime Compliance Logo" />
                  <p>Loading email history...</p>
                </div>
              </td>
            </tr>
          )}
          {!isLoading && (table.getRowModel().rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="email-table-empty-wrapper">
                <div className="email-table-empty-state">
                  <div className="empty-icon"><i className="bi bi-search"></i></div>
                  <h3>No emails found</h3>
                  <p>Your search or filter didn’t match any results.</p>
                </div>
              </td>
            </tr>
          ) : (
            table.getRowModel().rows.map(row => (
              <tr key={row.id}>
                {row.getVisibleCells().map(cell => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))
          ))}
        </tbody>
      </table>

      <div className="pagination-wrapper">
        <Pagination
          currentPage={currentPage}
          totalCount={filteredData.length}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>

      {selectedEmail && (
        <EmailDetailsModal
          email={selectedEmail}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}